import React, { useEffect, useContext, useState, useMemo, useRef } from 'react';

import { ConfigContext } from './ConfigContext';
import { LoginState } from '../../common/interface';
import {
  AURR_LOGIN_SCOPE,
  AURR_STATE_STORAGE_KEY,
  authInitialState,
  authCheckSession,
  authInitClient,
  authLogin,
  authLogout,
  getAuthState,
} from '../lib/auth';

const LoginContext = React.createContext({
  ...authInitialState,
  signIn: () => {},
  signOut: () => {},
});

interface LoginProviderProps {
  readonly children: React.ReactNode;
}

let generatedRedirectURI = '';
if (typeof window !== 'undefined') {
  generatedRedirectURI += window.location.origin;
  if (window.location.pathname.includes('autodesk-university')) {
    generatedRedirectURI += '/autodesk-university';
  }
  generatedRedirectURI += '/oxygen-login';
}

function LoginContextProvider({ children }: LoginProviderProps) {
  const { LOGIN_REDIRECT_URL, LOGIN_CLIENT_ID, LOGIN_CLIENT_ENV } =
    useContext(ConfigContext);

  const parsedRedirectUrl = LOGIN_REDIRECT_URL || generatedRedirectURI;
  const authInitializedRef = useRef(false);

  const [state, dispatch] = useState(
    typeof window !== 'undefined' ? getAuthState() : authInitialState
  );

  /**
   * Displays the SSO sign in form
   * @returns {undefined}
   */
  const signIn = async () => {
    dispatch((prevState: LoginState) => ({
      ...prevState,
      isUserLoading: true,
    }));

    await authLogin();
  };

  /**
   * Destroys token and logs user out of SSO
   */
  const signOut = async () => {
    dispatch((prevState: LoginState) => ({
      ...prevState,
      isUserLoading: true,
    }));
    await authLogout();
  };

  const refreshToken = () => {
    dispatch((prevState: LoginState) => ({
      ...prevState,
      isUserLoading: true,
    }));

    return authCheckSession(state.loginEnv);
  };

  const aurrAuthUpdate = async () => {
    let newIsLoading = false;
    let cachedState = getAuthState();
    if (!cachedState) {
      cachedState = authInitialState;
      // No cached state provided, assume we are still loading;
      newIsLoading = true;
    }

    dispatch((prevState: LoginState) => ({
      ...prevState,
      ...cachedState,
      isUserLoading: newIsLoading,
    }));
  };

  /**
   * Monitor localStorage updates to keep user sessions in sync across tabs
   */
  const handleStorageUpdated = (e: StorageEvent) => {
    if (e.key === AURR_STATE_STORAGE_KEY) {
      aurrAuthUpdate();
    }
  };

  // add/remove event listeners on component mount/unmount
  useEffect(() => {
    aurrAuthUpdate();
    window.addEventListener('aurrSignIn', signIn);
    window.addEventListener('aurrSignOut', signOut);
    window.addEventListener('aurrRefresh', refreshToken);
    window.addEventListener('aurrIdentityContextEvent', aurrAuthUpdate);
    window.addEventListener('storage', handleStorageUpdated);
    return () => {
      window.removeEventListener('aurrSignIn', signIn);
      window.removeEventListener('aurrSignOut', signOut);
      window.removeEventListener('aurrRefresh', refreshToken);
      window.removeEventListener('aurrIdentityContextEvent', aurrAuthUpdate);
      window.removeEventListener('storage', handleStorageUpdated);
    };
  }, []);

  const loginContextProviderState = useMemo(
    () => ({
      ...state,
      signIn,
      signOut,
    }),
    [state]
  );

  const isRedirectPage =
    typeof window !== 'undefined' &&
    window.location.origin + window.location.pathname === parsedRedirectUrl;

  useEffect(() => {
    // Prevent multiple initializations in StrictMode
    if (authInitializedRef.current) {
      return;
    }

    // Prevent any authentication checking for non-human sessions.
    const isBot = /bot|googlebot|crawler|spider|robot|crawling/i.test(
      window.navigator.userAgent
    );

    if (isBot || window.self !== window.top) {
      return;
    }

    if (window.isCheckingSession) {
      return;
    }

    authInitializedRef.current = true;

    authInitClient({
      client_id: LOGIN_CLIENT_ID,
      redirect_uri: parsedRedirectUrl,
      env: LOGIN_CLIENT_ENV,
      scope: AURR_LOGIN_SCOPE.join(' '),
      useRefreshTokens: true,
      isRedirectPage,
    });
  }, []);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // when token changes, set a timer to refresh the token before expiry
    if (state?.expires_on) {
      // Randomize refresh token time between 1 and 4 min from the received "exires_on" variable
      const timeout =
        state.expires_on -
        Date.now() -
        (Math.floor(Math.random() * 240) + 60) * 1000;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(refreshToken, timeout);
    }

    // Clear the timer when the component unmounts
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [state?.expires_on]);

  return (
    <LoginContext.Provider value={loginContextProviderState}>
      {children}
    </LoginContext.Provider>
  );
}

export default LoginContext;
export { LoginContextProvider };
